import { useState } from "react";
import { Loader2 } from "lucide-react";
import { useRestoreDoc } from "@/features/common/api/useRestoreDoc";
import { useDeleteSafetyCheck } from "@/features/common/api/useDeleteSafetyCheck";
import { useDeleteItem } from "@/features/inventory/api/useDeleteItem";
import { PermanentDeleteConfirm } from "@/features/common/components/archive-delete";
import type { ShopifySectionWidgetProps } from "../types";

/**
 * DisabledBanner — archive-aware banner for disabled/archived items.
 *
 * Shows "archived" messaging with Restore + conditional Delete buttons.
 * Self-contained: uses formData.name + internal hooks. No custom props
 * needed — the widget system only passes ShopifySectionWidgetProps.
 */
export function DisabledBanner({
  formData,
}: ShopifySectionWidgetProps) {
  const itemName = (formData?.name as string) ?? "";
  const restoreMutation = useRestoreDoc("Item");
  const safetyCheck = useDeleteSafetyCheck();
  const deleteMutation = useDeleteItem(itemName);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [canDelete, setCanDelete] = useState<boolean | null>(null);

  // Early return AFTER hooks (React rules of hooks)
  if (formData?.disabled !== 1) return null;

  const handleRestore = () => {
    if (!itemName) return;
    restoreMutation.mutate({ name: itemName });
  };

  const handleDeleteClick = () => {
    if (!itemName) return;
    // On-demand safety check when user clicks Delete on the banner
    safetyCheck.mutate(
      { doctype: "Item", name: itemName },
      {
        onSuccess: (data) => {
          if (data.can_delete) {
            setCanDelete(true);
            setShowDeleteConfirm(true);
          } else {
            // Item has transactions — hide Delete button
            setCanDelete(false);
          }
        },
        onError: () => {
          setCanDelete(true);
          setShowDeleteConfirm(true);
        },
      },
    );
  };

  const handlePermanentDelete = () => {
    deleteMutation.mutate();
    // useDeleteItem handles toast + navigation
  };

  const showDeleteButton = canDelete !== false;

  return (
    <>
      <div
        role="alert"
        className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 flex items-center gap-3"
      >
        <span className="text-sm text-amber-800 flex-1">
          This product is archived and hidden from your product list.
        </span>

        {showDeleteButton && (
          <button
            type="button"
            onClick={handleDeleteClick}
            disabled={safetyCheck.isPending}
            className="shrink-0 text-sm text-amber-700 underline hover:text-amber-900 disabled:opacity-50 transition-colors"
          >
            {safetyCheck.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              "Delete"
            )}
          </button>
        )}

        <button
          type="button"
          onClick={handleRestore}
          disabled={restoreMutation.isPending}
          className="shrink-0 rounded bg-amber-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50 transition-colors"
        >
          {restoreMutation.isPending ? "Restoring..." : "Restore"}
        </button>
      </div>

      <PermanentDeleteConfirm
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        itemName={itemName}
        doctype="Item"
        onConfirm={handlePermanentDelete}
        isDeleting={deleteMutation.isPending}
      />
    </>
  );
}
